// 从 URL 获取文件名的工具方法
export function getFileNameFromUrl(url: string): string {
  try {
    // 创建 URL 对象
    const urlObj = new URL(url);
    // 获取路径名
    const pathname = urlObj.pathname;
    // 获取最后一个斜杠后的内容作为文件名
    const fileName = pathname.split('/').pop() || 'unknown';
    return decodeURIComponent(fileName);
  } catch (error) {
    console.error('Invalid URL:', error);
    return 'unknown';
  }
}

/**
 * 获取文件后缀
 * @param fileType 文件类型 如 image/png
 * @returns 文件后缀 如 png
 */
export function getFileExtension(fileType: string): string | null {
  return fileType.split('/').pop() || null;
}

const MAX_IMAGE_DIMENSION = 1920;
const COMPRESS_DIMENSIONS = [MAX_IMAGE_DIMENSION, 1280, 1024];
const COMPRESS_QUALITIES = [0.8, 0.6, 0.5];

interface ImageDimensions {
  width: number;
  height: number;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('图片读取失败'));
    };
    image.src = objectUrl;
  });
}

export async function isImageAspectRatioMatching(
  file: File,
  targetAspect: number,
  tolerance = 0.02,
): Promise<boolean> {
  if (targetAspect <= 0) {
    return false;
  }

  const image = await loadImage(file);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;

  if (!width || !height) {
    return false;
  }

  const aspect = width / height;
  return Math.abs(aspect - targetAspect) / targetAspect <= tolerance;
}

function getTargetDimensions(width: number, height: number, maxDimension: number): ImageDimensions {
  const scale = Math.min(1, maxDimension / Math.max(width, height));

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function getJpegFileName(fileName: string): string {
  const nameWithoutExtension = fileName.replace(/\.[^./]+$/, '');
  return `${nameWithoutExtension || 'image'}.jpg`;
}

function canvasToFile(
  image: HTMLImageElement,
  dimensions: ImageDimensions,
  file: File,
  mimeType: string,
  quality: number,
): Promise<File> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    const context = canvas.getContext('2d');
    if (!context) {
      reject(new Error('浏览器不支持图片压缩'));
      return;
    }

    if (mimeType === 'image/jpeg') {
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('图片压缩失败'));
          return;
        }

        resolve(
          new File([blob], mimeType === 'image/jpeg' ? getJpegFileName(file.name) : file.name, {
            type: mimeType,
            lastModified: Date.now(),
          }),
        );
      },
      mimeType,
      quality,
    );
  });
}

/**
 * 在尽量保留图片分辨率、格式和质量的前提下，将图片压缩到指定体积以内。
 */
export async function compressImageToLimit(file: File, maxBytes: number): Promise<File> {
  const image = await loadImage(file);
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const sourceMaxDimension = Math.max(sourceWidth, sourceHeight);

  if (sourceMaxDimension <= MAX_IMAGE_DIMENSION && file.size <= maxBytes) {
    return file;
  }

  const dimensions = COMPRESS_DIMENSIONS.map((maxDimension) =>
    getTargetDimensions(sourceWidth, sourceHeight, maxDimension),
  ).filter(
    (item, index, items) =>
      index === 0 ||
      item.width !== items[index - 1].width ||
      item.height !== items[index - 1].height,
  );
  const sourceMimeType = file.type || 'image/jpeg';
  const isPng = sourceMimeType === 'image/png';
  let smallestFile = file;

  for (const targetDimensions of dimensions) {
    const originalFormatQualities = isPng ? [0.8] : COMPRESS_QUALITIES;

    for (const quality of originalFormatQualities) {
      const compressedFile = await canvasToFile(
        image,
        targetDimensions,
        file,
        sourceMimeType,
        quality,
      );
      if (compressedFile.size < smallestFile.size) {
        smallestFile = compressedFile;
      }
      if (compressedFile.size <= maxBytes) {
        return compressedFile;
      }
    }

    if (isPng) {
      for (const quality of COMPRESS_QUALITIES) {
        const compressedFile = await canvasToFile(
          image,
          targetDimensions,
          file,
          'image/jpeg',
          quality,
        );
        if (compressedFile.size < smallestFile.size) {
          smallestFile = compressedFile;
        }
        if (compressedFile.size <= maxBytes) {
          return compressedFile;
        }
      }
    }
  }

  return smallestFile;
}
